'use client'

import { SaveStudyWordButton } from '@/components/study/save-study-word-button'
import { WordFamiliarityBadge } from '@/components/study/word-familiarity-badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  EmphasisText,
  LabelText,
  MinorHeading,
  MutedText
} from '@/components/ui/typography'
import { useAudio } from '@/hooks/useAudio'
import { useQuickSearch } from '@/hooks/useQuickSearch'
import { useSettings } from '@/hooks/useSettings'
import { Link } from '@/i18n/routing'
import { useChineseFont } from '@/lib/chinese-font'
import { cn } from '@/lib/utils'
import {
  IconArrowLeft,
  IconExternalLink,
  IconVolume
} from '@tabler/icons-react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import type { Example, SearchResult } from '../../types/dictionary'
import { CharacterDrawingTab } from './character-drawing-tab'
import { CharacterPreview } from './character-preview'
import { CharsTab } from './chars-tab'
import { SelectableText } from './selectable-text'
import { WordsTab } from './words-tab'

interface Props {
  entry: SearchResult // The full row data from DB
  onSelectEntry?: (entry: { id: number }) => void
  onSearch?: (text: string) => void
  fromId?: number // The entry we came from (for back navigation)
  onBack?: () => void
  onSetRadicalFilter?: (radical: number) => void
  publicHref?: string | null
  className?: string
}

type DisplayDefinition = {
  en: string
  pos: string | null
  examples: Example[]
}

export function EntryDetail({
  entry,
  onSelectEntry,
  onSearch,
  fromId,
  onBack,
  onSetRadicalFilter,
  publicHref,
  className
}: Props) {
  const t = useTranslations('entry')
  const tPos = useTranslations('pos')
  const simplified = entry.simplified
  const traditional = entry.traditional || entry.simplified

  // Definitions and examples now come directly from entry (from entry_translations)
  const definitions = entry.definitions || []
  const examples = entry.examples || []

  // Language-agnostic data from json_data
  const { pos_tags = [], tones = [] } = entry.json_data || {}

  const reading = {
    pinyin: entry.pinyin || '',
    zhuyin: entry.zhuyin || '',
    tones
  }

  // Examples use 'trans' for translation (generic key for multi-language)
  const entryExamples = examples
  const { settings } = useSettings()

  // --- Script & Display Logic ---

  const getPrimaryScript = () => {
    if (settings.scriptDisplay === 'traditional' && traditional) {
      return traditional
    }
    if (settings.scriptDisplay === 'simplified') {
      return simplified
    }
    // For 'both', use priority
    if (settings.scriptDisplay === 'both') {
      return settings.scriptPriority === 'traditional' && traditional
        ? traditional
        : simplified
    }
    return simplified
  }

  const getSecondaryScript = () => {
    if (settings.scriptDisplay !== 'both') return null
    const primary = getPrimaryScript()
    if (primary === simplified && traditional && traditional !== simplified) {
      return traditional
    }
    if (primary === traditional) {
      return simplified
    }
    return null
  }

  const primaryScript = getPrimaryScript()
  const secondaryScript = getSecondaryScript()
  const showSecondary = secondaryScript !== null

  // Determine script types for API/Logic
  const getScriptType = (isPrimary: boolean): 'simplified' | 'traditional' => {
    const primaryIsTrad =
      (settings.scriptDisplay === 'traditional' ||
        (settings.scriptDisplay === 'both' &&
          settings.scriptPriority === 'traditional')) &&
      traditional

    if (isPrimary) return primaryIsTrad ? 'traditional' : 'simplified'
    return primaryIsTrad ? 'simplified' : 'traditional'
  }

  const primaryScriptType = getScriptType(true)
  const secondaryScriptType = getScriptType(false)

  // Initialize audio with script preference
  const { play, isPlaying, stuckMessage } = useAudio(primaryScriptType)

  useEffect(() => {
    if (stuckMessage) {
      toast.warning(stuckMessage, { duration: 6000 })
    }
  }, [stuckMessage])

  // For audio, use the primary script
  const isTraditionalForComponents = primaryScriptType === 'traditional'

  const targetWord =
    isTraditionalForComponents && traditional ? traditional : simplified

  const chineseFont = useChineseFont()
  // --- Selection Logic ---

  type SelectionState = {
    text: string
    indices: { start: number; end: number } | undefined
    scriptType: 'simplified' | 'traditional'
  }

  const [selection, setSelection] = useState<SelectionState>({
    text: '',
    indices: undefined,
    scriptType: 'simplified'
  })

  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false)

  const {
    results: searchResults,
    isLoading: searchIsLoading,
    search: performSearch
  } = useQuickSearch()

  const handleSelect = (
    text: string,
    startIndex: number,
    endIndex: number,
    scriptType: 'simplified' | 'traditional'
  ) => {
    if (text) {
      setSelection({
        text,
        indices: { start: startIndex, end: endIndex },
        scriptType
      })
      performSearch(text, scriptType)
    } else {
      clearSelection()
    }
  }

  const clearSelection = () => {
    setSelection({
      text: '',
      indices: undefined,
      scriptType: 'simplified' // Default, doesn't matter when empty
    })
    performSearch('', undefined)
    setCopyTooltipOpen(false)
  }

  const handleCopy = () => {
    setCopyTooltipOpen(true)
    setTimeout(() => setCopyTooltipOpen(false), 2000)
  }
  // --- Render Helpers ---

  // pos_tags is now a simple string array from json_data
  const posTags = pos_tags.filter((p): p is string => !!p)
  const simpleDefinitions = (definitions || []).map((en: string) => ({
    en,
    pos: null as string | null,
    examples: [] as Example[]
  }))
  const definitionsForDisplay: DisplayDefinition[] = simpleDefinitions

  const POS_KEY_MAP: Record<string, string> = {
    Noun: 'noun',
    Verb: 'verb',
    Modifier: 'modifier',
    Complement: 'complement',
    Pronoun: 'pronoun',
    Conjunction: 'conjunction',
    Particle: 'particle',
    'Measure Word': 'measureWord',
    Interjection: 'interjection',
    Affix: 'affix',
    Expression: 'expression',
    Miscellaneous: 'misc'
  }

  const translatePos = (pos: string) => {
    const key = POS_KEY_MAP[pos] || POS_KEY_MAP[pos.trim()]
    if (key) {
      const translated = tPos(key)
      return translated || pos
    }
    return pos
  }

  // Group definitions by POS, ensuring all definitions with the same POS are grouped together
  // Definitions without POS are grouped together and placed at the bottom
  const groupedDefs = definitionsForDisplay.reduce(
    (acc, def) => {
      const posKey = def.pos || null // Use null for no POS instead of string
      const existingGroup = acc.find((group) => group.pos === posKey)

      if (existingGroup) {
        existingGroup.items.push(def)
      } else {
        acc.push({ pos: posKey, items: [def] })
      }
      return acc
    },
    [] as { pos: string | null; items: DisplayDefinition[] }[]
  )

  // Sort groups: POS groups first (alphabetically), then no-POS group at the bottom
  groupedDefs.sort((a, b) => {
    // If both have POS, sort alphabetically
    if (a.pos && b.pos) {
      return a.pos.localeCompare(b.pos)
    }
    // If only a has POS, a comes first
    if (a.pos && !b.pos) {
      return -1
    }
    // If only b has POS, b comes first
    if (!a.pos && b.pos) {
      return 1
    }
    // Both have no POS, keep original order
    return 0
  })

  // Map null POS to display label
  const groupedDefsWithLabels = groupedDefs.map((group) => ({
    ...group,
    posLabel: group.pos || 'Definition'
  }))

  const getPronunciationDisplay = () => {
    if (settings.pronunciationDisplay === 'pinyin') {
      return { first: reading.pinyin, second: null, showBoth: false }
    }
    if (settings.pronunciationDisplay === 'zhuyin' && reading.zhuyin) {
      return { first: reading.zhuyin, second: null, showBoth: false }
    }
    // Both mode
    if (settings.pronunciationPriority === 'pinyin') {
      return {
        first: reading.pinyin,
        second: reading.zhuyin || null,
        showBoth: true
      }
    } else {
      return {
        first: reading.zhuyin || reading.pinyin,
        second: reading.zhuyin ? reading.pinyin : null,
        showBoth: !!reading.zhuyin
      }
    }
  }

  const pronunciation = getPronunciationDisplay()

  return (
    <div
      className={cn(
        'bg-card text-card-foreground max-w-full min-w-0 overflow-x-hidden rounded-2xl border p-4 shadow-sm sm:p-5 md:p-7',
        className
      )}
    >
      {fromId && onBack && (
        <Button
          variant="ghost"
          size="sm"
          className="mb-4 gap-2"
          onClick={onBack}
        >
          <IconArrowLeft className="h-4 w-4" />
          Back
        </Button>
      )}

      {/* Header Section */}
      <div className="border-border mb-4 border-b border-dashed pb-4 sm:mb-6 sm:pb-6">
        {/* Character and Pronunciation Row */}
        <div className="flex flex-wrap items-start justify-between gap-3 sm:gap-4">
          {/* Characters and Pronunciation Container */}
          <div className="mb-3 flex max-w-full min-w-0 flex-1 flex-col gap-3 md:mb-4 md:flex-row md:flex-wrap md:items-start md:gap-6">
            <div className="flex min-w-0 flex-col gap-3 md:max-w-full md:min-w-0">
              {/* Main Character */}
              <div className="max-w-full min-w-0 wrap-break-word">
                <CharacterPreview
                  selectedText={
                    selection.scriptType === primaryScriptType
                      ? selection.text
                      : ''
                  }
                  results={searchResults}
                  isLoading={searchIsLoading}
                  contextPinyin={
                    selection.indices &&
                    selection.indices.start === selection.indices.end
                      ? reading.pinyin.split(/\s+/)[selection.indices.start]
                      : undefined
                  }
                  onSearch={onSearch}
                  onClose={clearSelection}
                  onClearSelection={clearSelection}
                  copyTooltipOpen={copyTooltipOpen}
                  onTooltipOpenChange={setCopyTooltipOpen}
                >
                  <SelectableText
                    text={primaryScript}
                    tones={reading.tones}
                    size="xl"
                    scriptType={primaryScriptType}
                    onSelect={(text, start, end) =>
                      handleSelect(text, start, end, primaryScriptType)
                    }
                    selectedText={
                      selection.scriptType === primaryScriptType
                        ? selection.text
                        : ''
                    }
                    selectedIndices={
                      selection.scriptType === primaryScriptType
                        ? selection.indices
                        : undefined
                    }
                    onCopy={handleCopy}
                  />
                </CharacterPreview>
              </div>

              {/* Secondary Script */}
              {showSecondary && secondaryScript && (
                <div className="min-w-0 wrap-break-word">
                  <CharacterPreview
                    selectedText={
                      selection.scriptType === secondaryScriptType
                        ? selection.text
                        : ''
                    }
                    results={searchResults}
                    isLoading={searchIsLoading}
                    contextPinyin={
                      selection.indices &&
                      selection.indices.start === selection.indices.end
                        ? reading.pinyin.split(/\s+/)[selection.indices.start]
                        : undefined
                    }
                    onSearch={onSearch}
                    onClose={clearSelection}
                    onClearSelection={clearSelection}
                    copyTooltipOpen={copyTooltipOpen}
                    onTooltipOpenChange={setCopyTooltipOpen}
                  >
                    <SelectableText
                      text={secondaryScript}
                      tones={reading.tones}
                      size="md"
                      scriptType={secondaryScriptType}
                      onSelect={(text, start, end) =>
                        handleSelect(text, start, end, secondaryScriptType)
                      }
                      selectedText={
                        selection.scriptType === secondaryScriptType
                          ? selection.text
                          : ''
                      }
                      selectedIndices={
                        selection.scriptType === secondaryScriptType
                          ? selection.indices
                          : undefined
                      }
                      className={cn(
                        chineseFont,
                        'text-muted-foreground/70 text-4xl'
                      )}
                      onCopy={handleCopy}
                    />
                  </CharacterPreview>
                </div>
              )}
            </div>

            {/* Pronunciation */}
            <div className="flex w-full min-w-0 flex-col gap-2 md:w-auto md:pt-1">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    // Pinyin uses UI font (Nunito), Zhuyin uses system fonts
                    pronunciation.first === reading.zhuyin
                      ? 'font-sans'
                      : 'font-ui',
                    'max-w-full min-w-0 text-3xl tracking-wide wrap-break-word'
                  )}
                >
                  {pronunciation.first}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={t('playAudio')}
                  onClick={() => play(targetWord)}
                  disabled={isPlaying}
                  className="h-11 w-11 shrink-0 rounded-full"
                >
                  <IconVolume className="h-5 w-5" />
                </Button>
              </div>
              {pronunciation.showBoth && pronunciation.second && (
                <LabelText
                  className={cn(
                    // Pinyin uses UI font (Nunito), Zhuyin uses system fonts
                    pronunciation.second === reading.zhuyin
                      ? 'font-sans'
                      : 'font-ui',
                    'text-xl wrap-break-word'
                  )}
                >
                  {pronunciation.second}
                </LabelText>
              )}
            </div>
          </div>
          {/* Actions */}
          <div className="flex shrink-0 flex-row flex-wrap items-center gap-2 sm:flex-col">
            <SaveStudyWordButton
              entry={{
                id: entry.id,
                simplified: entry.simplified,
                traditional: entry.traditional || entry.simplified,
                pinyin: entry.pinyin || '',
                zhuyin: entry.zhuyin || '',
                definitions: entry.definitions || []
              }}
              source={{ page: 'dictionary-entry' }}
              iconOnly
              size="icon-lg"
              className="h-11 w-11 rounded-full"
            />
            <WordFamiliarityBadge entryId={entry.id} />
            {publicHref && (
              <Button
                asChild
                variant="outline"
                size="icon-lg"
                className="h-11 w-11 rounded-full"
                aria-label="View public page"
              >
                <Link href={publicHref}>
                  <IconExternalLink className="h-4 w-4" />
                </Link>
              </Button>
            )}
          </div>
        </div>

        {/* Metadata */}
        {(entry.hsk !== null && entry.hsk !== undefined) ||
        (entry.tocfl !== null && entry.tocfl !== undefined) ? (
          <div className="flex flex-wrap items-center gap-2">
            {entry.hsk !== null && entry.hsk !== undefined && (
              <div className="bg-primary/10 text-primary inline-flex rounded-full px-3 py-1 text-xs font-bold tracking-wide uppercase">
                HSK {entry.hsk}
              </div>
            )}
            {entry.tocfl !== null && entry.tocfl !== undefined && (
              <div className="bg-primary/10 text-primary inline-flex rounded-full px-3 py-1 text-xs font-bold tracking-wide uppercase">
                TOCFL {entry.tocfl}
              </div>
            )}
          </div>
        ) : null}
      </div>

      <Tabs
        key={entry.id}
        defaultValue="dict"
        className="w-full max-w-full min-w-0 overflow-hidden"
      >
        <TabsList className="mb-6 grid w-full grid-cols-4">
          <TabsTrigger value="dict">{t('tabs.dictionary')}</TabsTrigger>
          <TabsTrigger value="chars">{t('tabs.characters')}</TabsTrigger>
          <TabsTrigger value="draw">{t('tabs.draw')}</TabsTrigger>
          <TabsTrigger value="words">{t('tabs.words')}</TabsTrigger>
        </TabsList>

        <TabsContent value="dict">
          <div className="max-w-full min-w-0 space-y-6">
            {posTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {posTags.map((pos) => (
                  <div
                    key={pos}
                    className="bg-primary/10 text-primary inline-flex rounded-full px-3 py-1 text-xs font-bold tracking-wide uppercase"
                  >
                    {translatePos(pos)}
                  </div>
                ))}
              </div>
            )}
            {groupedDefsWithLabels.map((group, i) => (
              <div
                key={i}
                className="bg-muted/40 max-w-full min-w-0 rounded-lg p-4"
              >
                <div className="bg-primary/10 text-primary mb-3 inline-flex rounded-full px-3 py-1 text-xs font-bold tracking-wide uppercase">
                  {group.posLabel}
                </div>

                <ol className="space-y-4 pl-1">
                  {group.items.map((def, j) => (
                    <li key={j} className="space-y-2">
                      <EmphasisText className="text-xl leading-relaxed [overflow-wrap:anywhere] break-words">
                        <span className="text-muted-foreground mr-2 text-base font-normal">
                          {j + 1}.
                        </span>
                        {def.en}
                      </EmphasisText>
                    </li>
                  ))}
                </ol>
              </div>
            ))}

            {/* Examples Section - Show separately after all definitions */}
            {entryExamples && entryExamples.length > 0 && (
              <div className="bg-muted/40 max-w-full min-w-0 rounded-lg p-4">
                <MinorHeading className="mb-4 text-xs tracking-wider uppercase">
                  {t('sentences')}
                </MinorHeading>
                <div className="max-w-full min-w-0 space-y-3">
                  {entryExamples.map((ex, k) => {
                    // Determine which script to display based on user preference
                    const displayChinese =
                      settings.scriptDisplay === 'traditional' && ex.t
                        ? ex.t
                        : ex.s
                    const showBoth =
                      settings.scriptDisplay === 'both' && ex.t && ex.t !== ex.s

                    return (
                      <Card
                        key={k}
                        className="border-border/70 bg-background/80 max-w-full min-w-0 overflow-hidden border-dashed py-0"
                      >
                        <CardContent className="max-w-full min-w-0 space-y-3 p-4">
                          <div
                            className={cn(
                              chineseFont,
                              'text-foreground text-xl leading-relaxed [overflow-wrap:anywhere] break-words'
                            )}
                          >
                            {displayChinese}
                          </div>
                          {showBoth && (
                            <div
                              className={cn(
                                chineseFont,
                                'text-muted-foreground/70 text-lg leading-relaxed [overflow-wrap:anywhere] break-words'
                              )}
                            >
                              {ex.s}
                            </div>
                          )}
                          <MutedText className="text-base leading-relaxed [overflow-wrap:anywhere] break-words">
                            {ex.trans}
                          </MutedText>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="chars">
          <CharsTab
            entry={entry}
            onSelectEntry={onSelectEntry}
            onSetRadicalFilter={onSetRadicalFilter}
          />
        </TabsContent>

        <TabsContent value="draw">
          <CharacterDrawingTab
            entry={entry}
            primaryText={primaryScript}
            primaryScriptType={primaryScriptType}
            secondaryText={showSecondary ? secondaryScript || null : null}
            secondaryScriptType={secondaryScriptType}
          />
        </TabsContent>

        <TabsContent value="words">
          <WordsTab entry={entry} onSelectEntry={onSelectEntry} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
